/**
 * Skeleton — primitiva de carga. Reemplaza el "spinner suelto + 'Cargando…'"
 * por una silueta del contenido que va a aparecer (H4 de docs/UX_VISION.md).
 *
 * Cada vista arma su propia composición con estas piezas, cerca del contenido
 * real que imita. El bloque que envuelve al skeleton debe llevar
 * `role="status"` + un texto accesible (los `<Skeleton>` son `aria-hidden`).
 *
 * Tokens neutrales (funciona sobre cualquier superficie, Contexto A o B).
 * `prefers-reduced-motion` → pulso suave en vez de shimmer.
 */
import React from 'react';
import './ui.css';

interface SkeletonProps {
  /** ancho: número = px, string = tal cual ('100%', '8rem'). Default 100%. */
  width?: number | string;
  /** alto: número = px, string = tal cual. Default 16. */
  height?: number | string;
  /** border-radius. Default 6. `'pill'` = 999px, `'circle'` = 50%. */
  radius?: number | string | 'pill' | 'circle';
  className?: string;
  style?: React.CSSProperties;
}

const resolveRadius = (r: SkeletonProps['radius']): string | number => {
  if (r === 'pill') return 999;
  if (r === 'circle') return '50%';
  return r ?? 6;
};

export const Skeleton: React.FC<SkeletonProps> = ({
  width = '100%',
  height = 16,
  radius = 6,
  className = '',
  style,
}) => (
  <span
    className={`ccui-skeleton ${className}`}
    aria-hidden="true"
    style={{ width, height, borderRadius: resolveRadius(radius), ...style }}
  />
);

/** Varias líneas de texto skeleton. La última sale más corta. */
export const SkeletonText: React.FC<{ lines?: number; gap?: number }> = ({
  lines = 3,
  gap = 8,
}) => (
  <span className="ccui-skeleton-text" style={{ gap }} aria-hidden="true">
    {Array.from({ length: lines }).map((_, i) => (
      <Skeleton key={i} height={12} width={i === lines - 1 ? '55%' : '100%'} />
    ))}
  </span>
);

/**
 * Skeleton de una vista tipo tabla del portal (Consultas, Todos los Pacientes,
 * Cobros…): fila de KPI chips + barra de filtros + N filas.
 */
export const TableSkeleton: React.FC<{ rows?: number; chips?: number }> = ({
  rows = 8,
  chips = 4,
}) => (
  <div className="ccui-table-skeleton" role="status" aria-label="Cargando">
    <div className="ccui-table-skeleton__chips">
      {Array.from({ length: chips }).map((_, i) => (
        <Skeleton key={i} width={120} height={64} radius={12} />
      ))}
    </div>
    <Skeleton height={40} radius={10} />
    <div className="ccui-table-skeleton__rows">
      {Array.from({ length: rows }).map((_, i) => (
        <Skeleton key={i} height={50} radius={8} />
      ))}
    </div>
  </div>
);

/**
 * Skeleton de una vista de detalle/ficha: header con avatar + quick-stats +
 * cards de datos.
 */
export const RecordSkeleton: React.FC = () => (
  <div className="ccui-record-skeleton" role="status" aria-label="Cargando">
    <div className="ccui-record-skeleton__head">
      <Skeleton width={64} height={64} radius="circle" />
      <div className="ccui-record-skeleton__head-text">
        <Skeleton width="45%" height={20} />
        <Skeleton width="30%" height={13} />
      </div>
    </div>
    <div className="ccui-record-skeleton__stats">
      {Array.from({ length: 4 }).map((_, i) => (
        <Skeleton key={i} height={56} radius={10} />
      ))}
    </div>
    <div className="ccui-record-skeleton__cards">
      {Array.from({ length: 4 }).map((_, i) => (
        <Skeleton key={i} height={120} radius={12} />
      ))}
    </div>
  </div>
);

export default Skeleton;
